import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';

const VIEWPORT_SIZE = 320;
const CROP_DIAMETER = 240;
const OUTPUT_SIZE = 512;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export default function ProfilePhotoCropModal({ open, onOpenChange, file, onConfirm }) {
  const [imageSrc, setImageSrc] = useState('');
  const [imageElement, setImageElement] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  const minZoom = useMemo(() => {
    if (!imageElement) return 1;
    const fitX = CROP_DIAMETER / imageElement.naturalWidth;
    const fitY = CROP_DIAMETER / imageElement.naturalHeight;
    return Math.max(fitX, fitY, 0.1);
  }, [imageElement]);

  const maxZoom = useMemo(() => Math.max(minZoom * 4, minZoom + 0.5), [minZoom]);

  const clampOffset = (nextX, nextY, nextZoom) => {
    if (!imageElement) return { x: 0, y: 0 };
    const displayW = imageElement.naturalWidth * nextZoom;
    const displayH = imageElement.naturalHeight * nextZoom;
    const maxX = Math.max(0, (displayW - CROP_DIAMETER) / 2);
    const maxY = Math.max(0, (displayH - CROP_DIAMETER) / 2);
    return {
      x: clamp(nextX, -maxX, maxX),
      y: clamp(nextY, -maxY, maxY)
    };
  };

  useEffect(() => {
    if (!file || !open) {
      setImageSrc('');
      setImageElement(null);
      setOffset({ x: 0, y: 0 });
      setZoom(1);
      return;
    }

    const src = URL.createObjectURL(file);
    setImageSrc(src);

    const img = new Image();
    img.onload = () => {
      setImageElement(img);
      const fitX = CROP_DIAMETER / img.naturalWidth;
      const fitY = CROP_DIAMETER / img.naturalHeight;
      const initialZoom = Math.max(fitX, fitY, 0.1);
      setZoom(initialZoom);
      setOffset({ x: 0, y: 0 });
    };
    img.src = src;

    return () => URL.revokeObjectURL(src);
  }, [file, open]);

  const handlePointerDown = (event) => {
    if (!imageElement) return;
    setDragging(true);
    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      ox: offset.x,
      oy: offset.y
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragging || !imageElement) return;
    const dx = event.clientX - dragStartRef.current.x;
    const dy = event.clientY - dragStartRef.current.y;
    const next = clampOffset(dragStartRef.current.ox + dx, dragStartRef.current.oy + dy, zoom);
    setOffset(next);
  };

  const handlePointerUp = () => {
    setDragging(false);
  };

  const handleZoomChange = (value) => {
    const nextZoom = value?.[0] ?? zoom;
    setZoom(nextZoom);
    setOffset((prev) => clampOffset(prev.x, prev.y, nextZoom));
  };

  const handleConfirm = () => {
    if (!imageElement) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const displayW = imageElement.naturalWidth * zoom;
    const displayH = imageElement.naturalHeight * zoom;
    const imageLeft = VIEWPORT_SIZE / 2 + offset.x - displayW / 2;
    const imageTop = VIEWPORT_SIZE / 2 + offset.y - displayH / 2;
    const cropLeft = (VIEWPORT_SIZE - CROP_DIAMETER) / 2;
    const cropTop = (VIEWPORT_SIZE - CROP_DIAMETER) / 2;

    const sx = (cropLeft - imageLeft) / zoom;
    const sy = (cropTop - imageTop) / zoom;
    const sSize = CROP_DIAMETER / zoom;

    ctx.drawImage(imageElement, sx, sy, sSize, sSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const croppedFile = new File([blob], `profile-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onConfirm(croppedFile);
      },
      'image/jpeg',
      0.92
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Ritaglia foto profilo</DialogTitle>
          <DialogDescription>Sposta e ridimensiona la foto per centrare il volto nel cerchio.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="relative mx-auto rounded-lg overflow-hidden bg-black touch-none"
            style={{ width: VIEWPORT_SIZE, height: VIEWPORT_SIZE, cursor: dragging ? 'grabbing' : 'grab' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onPointerLeave={handlePointerUp}
          >
            {imageSrc && imageElement && (
              <img
                src={imageSrc}
                alt="Anteprima ritaglio"
                draggable={false}
                className="absolute select-none pointer-events-none max-w-none"
                style={{
                  width: imageElement.naturalWidth * zoom,
                  height: imageElement.naturalHeight * zoom,
                  left: VIEWPORT_SIZE / 2 + offset.x - (imageElement.naturalWidth * zoom) / 2,
                  top: VIEWPORT_SIZE / 2 + offset.y - (imageElement.naturalHeight * zoom) / 2
                }}
              />
            )}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `radial-gradient(circle at center, transparent 0 ${CROP_DIAMETER / 2}px, rgba(0,0,0,0.5) ${CROP_DIAMETER / 2 + 1}px)`
              }}
            />
            <div
              className="absolute pointer-events-none border-2 border-white rounded-full shadow-[0_0_0_9999px_rgba(0,0,0,0.15)]"
              style={{
                width: CROP_DIAMETER,
                height: CROP_DIAMETER,
                left: (VIEWPORT_SIZE - CROP_DIAMETER) / 2,
                top: (VIEWPORT_SIZE - CROP_DIAMETER) / 2
              }}
            />
          </div>

          <div className="px-2">
            <div className="text-xs text-gray-500 mb-2">Zoom</div>
            <Slider
              min={minZoom}
              max={maxZoom}
              step={0.01}
              value={[zoom]}
              onValueChange={handleZoomChange}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={handleConfirm} disabled={!imageElement}>Conferma ritaglio</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
